import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject, throwError } from 'rxjs';
import { Calendar, CalendarEntry } from 'src/app/classes';
import * as moment from 'moment';
import { TokenService } from '../../shared/services/token.service';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { serverLocation } from 'src/app/globals';
import { ICalendarProviderService } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class CalendarService implements ICalendarProviderService {

  private _calendars: { [key: string]: ReplaySubject<Calendar> } = {};
  private _calendarsLoaded = new BehaviorSubject(false);
  public calendarsLoaded$ = this._calendarsLoaded.asObservable();

  public calendars$ = this.loadCalendars(true);

  private _calendarContents: { [key: string]: ReplaySubject<CalendarEntry[]> } = {};

  constructor(
    private _tokenService: TokenService,
    private _httpClient: HttpClient
  ) { }

  createEvent(event: CalendarEntry): Observable<any> {
    return this._httpClient.post(`${serverLocation}/api/nextcloud/calendar/event`, {
      id: event.id,
      title: event.title,
      start: moment(event.start).format("YYYYMMDDTHHmmss"),
      end: moment(event.end).format("YYYYMMDDTHHmmss"),
      href: event.href
    } as CalendarEntry, {
      headers: new HttpHeaders().set('Authorization', this._tokenService.token)
    });
  }

  loadCalendars(soft: boolean = true): Observable<Calendar[]> {
    let subject = new ReplaySubject<Calendar[]>();
    if (!soft || !this._calendarsLoaded.value) {
      this._loadCalendars().pipe(
        tap((calendars: Calendar[]) => {
          for (let calendar of calendars) {
            if (!this._calendars[calendar.href]) this._calendars[calendar.href] = new ReplaySubject(1);
            this._calendars[calendar.href].next(calendar);
          }
          this._calendarsLoaded.next(true);
        })
      ).subscribe(subject);
    }
    else combineLatest(Object.values(this._calendars)).subscribe(subject);
    return subject.asObservable();
  }

  loadCalendarContent(href: string, soft: boolean = true): Observable<CalendarEntry[]> {
    if (!href) return throwError('no calendar href passed');
    if (!soft || !this._calendarContents[href]) {
      if (!this._calendarContents[href]) this._calendarContents[href] = new ReplaySubject<CalendarEntry[]>();
      this._loadCalendarContent(href).subscribe({
        next: (calendarEntries: CalendarEntry[]) => this._calendarContents[href].next(calendarEntries),
        error: (httpError: HttpErrorResponse) => this._calendarContents[href].error(httpError)
      });
    }
    return this._calendarContents[href].asObservable();
  }

  private _loadCalendars(): Observable<Calendar[]> {
    return this._httpClient.get(`${serverLocation}/api/nextcloud/calendar`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Calendar[]>;
  }

  private _loadCalendarContent(href: string): Observable<CalendarEntry[]> {
    return this._httpClient.post(`${serverLocation}/api/nextcloud/calendar/content`, {
      path: href
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<CalendarEntry[]>;
  }
}
